import { CUSTOM_DATA_LAYER_ID_PREFIX, SITE_DATA_LAYER_ID_PREFIX } from '@civilgrid/utils/src/extract/constants';
import { uniq } from 'lodash';
import type { ExtractProjection } from '~/api/extract';
import type { ProjectCustomData } from '~/api/projectCustomData';
import type { ProjectDownload } from '../common';
import { CUSTOM_DATA_LAYER_FALLBACK_LABEL, LEGACY_PROJECT_OUTLINE_LAYER, toLayerId } from '../layerLabels';
import { type ExportRun, toExportRuns } from '../runs';
import { ExtractFormat, type TExtractFormat } from './common';
import { getSiteDataLayerLabel } from './siteDataLayers';

export function newestRunForProject(downloads: ProjectDownload[], projectId: string): ExportRun | undefined {
  const [newestRun] = toExportRuns(downloads);
  return newestRun?.projectId === projectId ? newestRun : undefined;
}

// properties for event export_form_rebuilt
export function buildFormRebuiltProperties(params: {
  projectId: string;
  organizationId: string;
  run: Pick<ExportRun, 'submissionId'>;
  rebuilt: RebuiltExport;
}) {
  const { rebuilt } = params;

  return {
    project_id: params.projectId,
    organization_id: params.organizationId || undefined,
    submission_id: params.run.submissionId,
    restored_layer_count: rebuilt.layerIds.length,
    dropped_layer_count: rebuilt.droppedLayerIds.length,
    dropped_layer_ids: rebuilt.droppedLayerIds,
    formats_restored: rebuilt.formats !== null,
    projection_restored: rebuilt.projectionId !== null,
    template_restored: rebuilt.templateRestored,
  };
}

export type RebuiltExport = {
  layerIds: string[];
  droppedLayerIds: string[];
  formats: TExtractFormat[] | null;
  projectionId: string | null;
  // Null is CivilGrid Default, including when the run's template is no longer offered.
  extractTemplateId: string | null;
  // False only when the run named a template the org no longer offers.
  templateRestored: boolean;
};

const EXTRACT_FORMATS = new Set<string>(Object.values(ExtractFormat));

// Legacy ESRI rows store the same formats lowercase (`dwg`, `shp`); a missing or retired one maps to null
const toExtractFormat = (format: string | null): TExtractFormat | null => {
  const normalized = format?.toUpperCase();
  return normalized && EXTRACT_FORMATS.has(normalized) ? (normalized as TExtractFormat) : null;
};

export type DownloadLayerMaps = {
  layerIdToDownloadLayer: Record<string, string>;
  overrides: Record<string, string>;
};

// Legacy ESRI runs store GPServer names, not picker ids
const toPickerLayerIds = (storedLayers: string[], maps: DownloadLayerMaps): string[] =>
  uniq(
    storedLayers
      .filter((layer) => layer !== LEGACY_PROJECT_OUTLINE_LAYER)
      .map((layer) => toLayerId(layer, maps.layerIdToDownloadLayer, maps.overrides, { acceptBaseName: true }) ?? layer),
  );

export function rebuildFromRun(
  run: Pick<ExportRun, 'layers' | 'formats' | 'projection' | 'extractTemplateId'>,
  options: {
    offeredLayerIds: Iterable<string>;
    projections: ExtractProjection[];
    templates: { extractTemplateId: string }[];
    multiFormat: boolean;
    layerMaps: DownloadLayerMaps;
  },
): RebuiltExport {
  const offered = new Set(options.offeredLayerIds);
  const runLayerIds = toPickerLayerIds(run.layers, options.layerMaps);

  const runFormats = uniq(
    run.formats.map((format) => toExtractFormat(format.format)).filter((format) => format !== null),
  );
  const formats = runFormats.length === 0 ? null : options.multiFormat ? runFormats : runFormats.slice(0, 1);

  const storedProjection = run.projection?.value;
  const projection = storedProjection
    ? (options.projections.find((option) => option.fmeFormat === storedProjection) ??
      // Legacy ESRI rows store the EPSG WKID, not the FME format
      options.projections.find((option) => option.epsgCode === storedProjection))
    : undefined;

  const storedTemplateId = run.extractTemplateId ?? null;
  const extractTemplateId =
    storedTemplateId && options.templates.some((template) => template.extractTemplateId === storedTemplateId)
      ? storedTemplateId
      : null;

  return {
    layerIds: runLayerIds.filter((layerId) => offered.has(layerId)),
    droppedLayerIds: runLayerIds.filter((layerId) => !offered.has(layerId)),
    formats,
    projectionId: projection?.id ?? null,
    extractTemplateId,
    templateRestored: storedTemplateId === null || extractTemplateId !== null,
  };
}

/** Display name for a layer the rebuild dropped; `undefined` leaves the caller to fall back to the id. */
export function labelForRebuiltLayerId(
  layerId: string,
  lookup: { customDataRows: readonly ProjectCustomData[] | undefined; tocLabels: ReadonlyMap<string, string> },
): string | undefined {
  if (layerId.startsWith(SITE_DATA_LAYER_ID_PREFIX)) return getSiteDataLayerLabel(layerId);
  if (!layerId.startsWith(CUSTOM_DATA_LAYER_ID_PREFIX)) return lookup.tocLabels.get(layerId);

  const rowId = layerId.slice(CUSTOM_DATA_LAYER_ID_PREFIX.length);
  return lookup.customDataRows?.find((row) => row.id === rowId)?.label ?? CUSTOM_DATA_LAYER_FALLBACK_LABEL;
}
